import type { JSX } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { Icon, type IconName } from "@frontend/components/atoms/Icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";

import { Button } from "./Button";
import { ButtonGroup } from "./ButtonGroup";
import {
  type ButtonGroupDataProps,
  buttonGroupDataSchema,
} from "./ButtonGroup.schema";
import { BUTTON_GROUP_ITEM_TAG_NAME, BUTTON_GROUP_TAG_NAME } from "./constants";

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [BUTTON_GROUP_TAG_NAME]: { props: ButtonGroupDataProps; handle: undefined };
  }
  interface InertTagNames {
    [BUTTON_GROUP_ITEM_TAG_NAME]: true;
  }
}

const RWCButtonGroupItem = rwc(BUTTON_GROUP_ITEM_TAG_NAME);

function readButtonGroupSlotted(
  el: HTMLElement,
): Partial<ButtonGroupDataProps> {
  const buttons = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCButtonGroupItem.tagName}`),
  ).map((item) => ({
    value: item.getAttribute("value") ?? "",
    label: item.textContent?.trim() || undefined,
    icon: item.getAttribute("icon") ?? undefined,
    ariaLabel: item.getAttribute("aria-label") ?? undefined,
  }));

  return {
    buttons,
    onButtonClick: (value) => {
      el.dispatchEvent(
        new CustomEvent("button-group:click", {
          detail: { value },
          bubbles: true,
        }),
      );
    },
  };
}

function RWCButtonGroupComponent({
  buttons = [],
  variant,
  size,
  disableRipple,
  onButtonClick,
}: Partial<ButtonGroupDataProps>): JSX.Element {
  return (
    <ButtonGroup>
      {buttons.map((item) => {
        const button = (
          <Button
            key={item.value}
            variant={variant}
            size={size}
            disableRipple={disableRipple}
            aria-label={item.ariaLabel}
            leftSection={
              item.icon ? (
                <Icon name={item.icon as IconName} size={size ?? "sm"} />
              ) : undefined
            }
            label={item.label}
            onClick={() => onButtonClick?.(item.value)}
          />
        );

        return item.ariaLabel && !item.label ? (
          <Tooltip key={item.value}>
            <TooltipTrigger render={button} />
            <TooltipContent>{item.ariaLabel}</TooltipContent>
          </Tooltip>
        ) : (
          button
        );
      })}
    </ButtonGroup>
  );
}

componentManager.register(
  RWCButtonGroupItem,
  rwc(BUTTON_GROUP_TAG_NAME, {
    component: RWCButtonGroupComponent,
    dataSchema: buttonGroupDataSchema,
    usesSlots: true,
    readSlotted: readButtonGroupSlotted,
  }),
);
